import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { endpoints } from '../../../environments/endpoints';
import type { BillingTerm } from '../models/pricing.model';
import type { RegionQuery } from './region.service';

export interface LiveRate {
  readonly amount: number;
  readonly currency: string;
  readonly interval: string;
}

export interface LivePlan {
  readonly code: string;
  readonly name: string;
  readonly userCap: number | null;
  readonly prices: Partial<Record<BillingTerm, LiveRate>>;
}

/** The market the last successful answer actually priced against. */
export interface LiveMarket {
  readonly code: string;
  readonly currency: string;
}

interface CatalogRatePrice {
  readonly offer_type: BillingTerm;
  readonly billing_interval: string;
  readonly currency: string;
  readonly amount: number;
}

interface CatalogRatePlan {
  readonly code: string;
  readonly name: string;
  readonly user_cap: number | null;
  readonly prices: readonly CatalogRatePrice[];
}

interface CatalogRatesResponse {
  readonly data: {
    readonly market?: LiveMarket;
    readonly plans: readonly CatalogRatePlan[];
  };
}

// Stripe's zero-decimal currencies — dividing these by 100 would corrupt the amount.
const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
]);

function toMajorUnits(amount: number, currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency) ? amount : amount / 100;
}

function toRates(prices: readonly CatalogRatePrice[]): Partial<Record<BillingTerm, LiveRate>> {
  const rates: Partial<Record<BillingTerm, LiveRate>> = {};
  for (const price of prices) {
    rates[price.offer_type] = {
      amount: toMajorUnits(price.amount, price.currency),
      currency: price.currency,
      interval: price.billing_interval,
    };
  }
  return rates;
}

function toLivePlan(plan: CatalogRatePlan): LivePlan {
  return {
    code: plan.code,
    name: plan.name,
    userCap: plan.user_cap,
    prices: toRates(plan.prices),
  };
}

function toPlansByUserCap(plans: readonly CatalogRatePlan[]): ReadonlyMap<number, LivePlan> {
  const byCap = new Map<number, LivePlan>();
  for (const plan of plans) {
    if (plan.user_cap !== null) byCap.set(plan.user_cap, toLivePlan(plan));
  }
  return byCap;
}

function toParams(query: RegionQuery): HttpParams {
  let params = new HttpParams();
  if (query.country) params = params.set('country', query.country);
  return params;
}

/** Overlays catalog-api's live prices onto the built-in fallback in `pricing.config.ts`. */
@Injectable({ providedIn: 'root' })
export class CatalogRatesService {
  private readonly http = inject(HttpClient);

  private readonly _plans = signal<ReadonlyMap<number, LivePlan> | null>(null);
  private readonly _source = signal<'config' | 'live'>('config');
  private readonly _market = signal<LiveMarket | null>(null);

  /** Null until the first successful answer. Null means "use the built-in figures". */
  readonly plans = this._plans.asReadonly();

  /** Where the amounts currently on screen came from. */
  readonly source = this._source.asReadonly();

  /** The market the amounts on screen are actually priced in. Null before the first answer. */
  readonly market = this._market.asReadonly();

  private lastQueryKey: string | null = null;
  private latestRequest = 0;

  /** Fetches published prices for the given region, or the platform default when none is given. */
  load(query: RegionQuery = {}): void {
    const key = query.country ?? '';
    if (key === this.lastQueryKey) return;
    this.lastQueryKey = key;

    const request = ++this.latestRequest;

    this.http
      .get<CatalogRatesResponse>(`${environment.api.host}/${endpoints.marketing.pricing}`, {
        params: toParams(query),
      })
      .subscribe({
        next: (response) => {
          // Responses can land out of order; a superseded one must not win.
          if (request !== this.latestRequest) return;

          const plans = response?.data?.plans;
          if (!plans?.length) return;

          const byCap = toPlansByUserCap(plans);
          if (!byCap.size) return;

          const market = response?.data?.market;
          if (market) this._market.set(market);

          this._plans.set(byCap);
          this._source.set('live');
        },
        // The built-in amounts are already on screen. Clearing the key lets a
        // later region retry rather than pinning the visitor to the fallback.
        error: () => {
          if (request === this.latestRequest) this.lastQueryKey = null;
        },
      });
  }
}
